import { createUseStyles, withTheme } from '@glu/theming';
import PropTypes from 'prop-types';
import React, { useRef, useEffect } from 'react';
import { CaretRightIcon } from '@glu/icons-react';
import { AccessibilityText } from '@glu/utilities-react';
import locale from '@glu/locale';
import classnames from 'classnames';
import Checkbox from './Checkbox/Checkbox';
import Header from './Header';
import HighlightText from './HighlightText';
import styles from './Option.styles';

const useStyles = createUseStyles(styles);

const Option = (props) => {
  const {
    dark,
    filterString,
    handleKeyDown,
    htmlId,
    isCategory,
    isFocused,
    isSelected,
    multiselect,
    onChange,
    onClick,
    option,
    setFocus,
    showGroups
  } = props;

  const optionRef = useRef();
  const classes = useStyles(props);
  const OptionTabIndex = isSelected ? '0' : '-1';

  useEffect(() => {
    setFocus(option, isFocused, optionRef);
  });

  return multiselect
    ? (
      <div
        data-qa={`option-wrapper-${option.id}`}
        className={`
          ${classes.root}
          ${classes[`depth${option.depth}`]}
          ${dark ? classes.dark : ''}
          ${isSelected ? classes.selected : ''}
          ${option.disabled ? classes.disabled : ''}
          ${option.highlight ? classes.highlight : ''}
        `}
      >
        <div ref={optionRef}>
          <Checkbox
            checked={isSelected}
            dataQa={`option-checkbox-${option.id}`}
            disabled={option.disabled}
            htmlId={htmlId}
            labelText={<HighlightText string={option.name} toMatch={filterString} />}
            name={option.name}
            onChange={onChange}
            classes={classes}
            onKeyDown={(event) => { handleKeyDown(event, option, true); }}
          />
        </div>

        {(isCategory && !showGroups) && (
        <button
          aria-selected={isSelected}
          className={classes.categoryButton}
          data-qa={`option-button-${option.id}`}
          disabled={option.disabled}
          id={`${htmlId}-option-button-${option.id}`}
          onClick={() => { onClick(option, true); }}
          onKeyDown={(event) => { handleKeyDown(event, option, true); }}
          role="option"
          tabIndex={OptionTabIndex}
          type="button"
        >
          <AccessibilityText>{`${option.name} ${locale.get('category')}`}</AccessibilityText>
          <CaretRightIcon className={classnames(classes.icon, { [classes.dark]: dark })} />
        </button>
        )}
      </div>
    )
    : (isCategory && showGroups)
      ? <Header depth={option.depth}>{option.name}</Header>
      : (
        <div
          data-qa={`option-wrapper-${option.id}`}
          className={`
          ${classes.singleOptionWrapper}
        `}
        >
          <button
            aria-selected={isSelected}
            id={`${htmlId}-category-option-button-${option.id}`}
            className={`
            ${classes.root}
            ${classes[`depth${option.depth}`]}
            ${dark ? classes.dark : ''}
            ${isSelected ? classes.selected : ''}
            ${option.disabled ? classes.disabled : ''}
            ${option.highlight ? classes.highlight : ''}
          `}
            title={option.name}
            data-qa={`option-button-${option.id}`}
            disabled={option.disabled}
            onClick={() => { onClick(option, false); }}
            onKeyDown={(event) => { handleKeyDown(event, option); }}
            ref={optionRef}
            role="option"
            tabIndex={isSelected ? '0' : '-1'}
            type="button"
          >
            <HighlightText string={option.name} toMatch={filterString} />
          </button>
          {
            (isCategory && !showGroups) && (
              <button
                aria-selected={isSelected}
                className={classes.categoryButton}
                data-qa={`option-category-button-${option.id}`}
                disabled={option.disabled}
                id={`${htmlId}-option-button-${option.id}`}
                onClick={() => { onClick(option, true); }}
                onKeyDown={(event) => { handleKeyDown(event, option, true); }}
                role="option"
                tabIndex={OptionTabIndex}
                type="button"
              >
                <AccessibilityText>{`${option.name} ${locale.get('category')}`}</AccessibilityText>
                <CaretRightIcon className={classnames(classes.icon, { [classes.dark]: dark })} />
              </button>
            )
          }
        </div>
      );
};

Option.propTypes = {
  dark: PropTypes.bool.isRequired,
  handleKeyDown: PropTypes.func.isRequired,
  htmlId: PropTypes.string.isRequired,
  isCategory: PropTypes.bool.isRequired,
  isFocused: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,

  /** The user's typed input which options are filtered by  */
  filterString: PropTypes.string.isRequired,

  /** UX setting to allow multiple selections */
  multiselect: PropTypes.bool.isRequired,

  /** Object representing a single option */
  option: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    highlight: PropTypes.bool,
    depth: PropTypes.number
  }).isRequired,

  /**
   * Funtion to assign focus based on disabled or focused state of an option.
   * @param {obj} option
   * @param {bool} focused
   * @param {ref} optionRef
   * */
  setFocus: PropTypes.func.isRequired,

  /** Display setting for representing parent options as flat headers */
  showGroups: PropTypes.bool.isRequired
};
export default withTheme(Option);
